import React from 'react';
import styled from 'styled-components';
import { Modal, ModalFooter } from './Modal';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in the error/danger color. */
  destructive?: boolean;
  busy?: boolean;
  /** Base for data-testid: dialog gets `{testIdPrefix}`, buttons get `{testIdPrefix}-confirm` / `-cancel`. */
  testIdPrefix: string;
}

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const CancelButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button<{ $destructive?: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  border: none;
  color: ${({ theme }) => theme.colors.textInverse};
  background: ${({ theme, $destructive }) => ($destructive ? theme.colors.error : theme.colors.primary)};
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * Plain yes/no confirmation dialog. For flows that require the user to type
 * a free-text reason (freeze/cancel-with-reason), use ConfirmReasonModal
 * instead — this component intentionally has no reason field.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  testIdPrefix,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" data-testid={testIdPrefix}>
      {description && <Description>{description}</Description>}
      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={busy} data-testid={`${testIdPrefix}-cancel`}>
          {cancelLabel}
        </CancelButton>
        <ConfirmButton
          type="button"
          $destructive={destructive}
          onClick={onConfirm}
          disabled={busy}
          data-testid={`${testIdPrefix}-confirm`}
        >
          {busy ? 'Working…' : confirmLabel}
        </ConfirmButton>
      </ModalFooter>
    </Modal>
  );
};
